import React, { useCallback } from 'react'
import { FileText, Trash2, Tag, AlertCircle, Loader2, AlertTriangle } from 'lucide-react'
import styled, { keyframes } from 'styled-components'
import { Tooltip } from 'antd'
import { BORDER_RADIUS, TYPOGRAPHY, FONT_WEIGHT } from '../../styles/theme'
import type { VentureDocument } from '../../services/documents/documentService'
import type {
  DocumentSummaryType,
  SurveySummaryStructured,
  InterviewSummaryStructured,
} from '@bandos/shared'
import {
  ResearchCard,
  CardHeader,
  CardIconWrapper,
  CardInfo,
  CardLabel,
  CardStats,
  StatChip,
  CardActions,
  CardActionButton,
} from './ResearchPageStyles'

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`

const SpinningLoader = styled(Loader2)`
  animation: ${spin} 1s linear infinite;
  color: var(--color-text-tertiary);
`

const ClassificationChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 1px 7px;
  border-radius: ${BORDER_RADIUS.sm};
  background: var(--color-primary-bg, rgba(99, 102, 241, 0.08));
  color: var(--color-primary, #6366f1);
  font-size: 11px;
  font-weight: ${FONT_WEIGHT.medium};
  max-width: 220px;
  overflow: hidden;
`

const ClassificationChipText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`

const MismatchChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 1px 7px;
  border-radius: ${BORDER_RADIUS.sm};
  background: rgba(245, 158, 11, 0.1);
  color: #d97706;
  font-size: 11px;
  font-weight: ${FONT_WEIGHT.medium};
`

const FILE_ACCENT_COLOR = '#6366f1'

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export interface UploadedResearchItem {
  id: string
  file_name: string
  file_size: number
  status: 'processing' | 'ready' | 'error'
  content_classification: string | null
  error_message: string | null
  created_at: string
  ai_document_type: DocumentSummaryType | null
  ai_summary_structured: SurveySummaryStructured | InterviewSummaryStructured[] | null
}

export function ventureDocumentToUploadedItem(doc: VentureDocument): UploadedResearchItem {
  return {
    id: doc.id,
    file_name: doc.file_name,
    file_size: doc.file_size,
    status: doc.status,
    content_classification: doc.content_classification,
    error_message: doc.error_message,
    created_at: doc.created_at,
    ai_document_type: doc.ai_document_type,
    ai_summary_structured: doc.ai_summary_structured,
  }
}

interface UploadedResearchCardProps {
  item: UploadedResearchItem
  onDelete: (item: UploadedResearchItem) => void
  onClick?: (item: UploadedResearchItem) => void
}

export const UploadedResearchCard = React.memo(
  ({ item, onDelete, onClick }: UploadedResearchCardProps) => {
    const isClickable = item.ai_document_type === 'survey' || item.ai_document_type === 'interview'

    const handleDeleteClick = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation()
        onDelete(item)
      },
      [item, onDelete]
    )

    const handleClick = useCallback(() => {
      if (isClickable && onClick) {
        onClick(item)
      }
    }, [isClickable, onClick, item])

    const uploadDate = new Date(item.created_at).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })

    return (
      <ResearchCard $accentColor={FILE_ACCENT_COLOR} $clickable={isClickable} onClick={handleClick}>
        <CardHeader>
          <CardIconWrapper $color={FILE_ACCENT_COLOR}>
            <FileText size={16} color={FILE_ACCENT_COLOR} />
          </CardIconWrapper>
          <CardInfo>
            <CardLabel title={item.file_name}>{item.file_name}</CardLabel>
            <CardStats>
              {item.status === 'processing' && (
                <StatChip>
                  <SpinningLoader size={11} />
                  Analysing…
                </StatChip>
              )}

              {item.ai_document_type === 'mismatch' && (
                <Tooltip title="This file doesn't appear to contain the claimed data type. It has been embedded for RAG but won't contribute to confidence scoring.">
                  <MismatchChip>
                    <AlertTriangle size={10} style={{ flexShrink: 0 }} />
                    Doesn&apos;t match type
                  </MismatchChip>
                </Tooltip>
              )}

              {item.status === 'ready' &&
                item.ai_document_type !== 'mismatch' &&
                item.content_classification && (
                  <ClassificationChip title={item.content_classification}>
                    <Tag size={10} style={{ flexShrink: 0 }} />
                    <ClassificationChipText>{item.content_classification}</ClassificationChipText>
                  </ClassificationChip>
                )}

              {item.status === 'error' && (
                <StatChip style={{ color: 'var(--color-error, #ff4d4f)' }}>
                  <Tooltip title={item.error_message ?? 'Processing failed'}>
                    <AlertCircle size={11} />
                  </Tooltip>
                  Failed to process
                </StatChip>
              )}

              <StatChip>{formatFileSize(item.file_size)}</StatChip>
              <StatChip
                style={{
                  fontSize: TYPOGRAPHY.caption.fontSize,
                  color: 'var(--color-text-tertiary)',
                }}
              >
                {uploadDate}
              </StatChip>
            </CardStats>
          </CardInfo>
        </CardHeader>

        <CardActions onClick={(e) => e.stopPropagation()}>
          <CardActionButton type="button" onClick={handleDeleteClick} title="Delete file">
            <Trash2 size={13} />
          </CardActionButton>
        </CardActions>
      </ResearchCard>
    )
  }
)

UploadedResearchCard.displayName = 'UploadedResearchCard'
